import store from '../../../store/store';
import { DocumentType, PdpSmallCardTemplateParams, ProductType } from '../interfaces/product-card.interface';
import { OfferTypeChecker } from '../../../common/utils/offer-type-util';
import { ProductCardData, ProductSmallCardData } from '../product-card-display';
import { StoreState } from '../../../store/store.interface';
import { emitProductViewAnalyticsEvent } from './product-card.analytics.util';
import { fetchParentProductProgramId, savePdpLocalStorageObject } from '../../search/utils/search.util';
import { isHEInstructor } from '../../../common/utils/cart-util';
import { selectCurrentPageIndex, selectCurrentPageLength } from '../../search/store/search.selector';
import { getUserDataForUserAnalytics, selectUser } from '../../user-profile/user-profile.selectors';
import { html, nothing, TemplateResult } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import heading from '../../../common/headings';
import { getCartAnalyticsOverrides } from '../../cart/shop-cart/shop-cart.analytics';
import { getCreateCustomImage } from '../../create-custom/utils/create-custom-product.util';

/**
 * Retrieves the product URL.
 *
 * @param product
 */
export function getProductUrl(product) {
  let baseUrl = '';

  switch (product.type) {
    case ProductType.he:
      baseUrl = `/highered/product/${product?.optimizedUniqueTitle}.html`;
      break;
    case ProductType.segProduct:
    case ProductType.segProgram:
      switch (product.documentType) {
        case DocumentType.programs:
          baseUrl = getProgramUrl(product?.programId);
          break;
        case DocumentType.products:
        default:
          baseUrl = `/prek-12/product/${product?.isbn13 || product?.isbn}.html`;
          break;
      }
      break;
  }

  // Add instructor query parameter for guest users and logged-in HE instructors
  if (baseUrl && product.type === ProductType.he) {
    const state = store.getState() as any;
    const user = selectUser(state);
    const isGuestOrHeInstructor = !user || isHEInstructor(user);

    if (isGuestOrHeInstructor) {
      baseUrl += '?viewOption=instructor';
    }
  }

  return baseUrl;
}

/**
 * Retrieves the program URL
 *
 * @param programID
 */
export function getProgramUrl(programID): string {
  return `/prek-12/program/${programID}.html`;
}

/**
 * PDP redirection.
 *
 * @param productData
 */
export function goToProductPage(productData: ProductCardData): void {
  const url = getProductUrl(productData);
  window.location.href = url;
}

/**
 * Cleans up the product title by removing special characters and spaces, and appending the identifier if available.
 *
 * @param identifier
 * @param title
 */
export function getSanitizedProductTitle(identifier?: string, title?: string): string {
  const sanitizedTitle = (title ?? '').replace(/[^a-zA-Z0-9]/g, '');
  const suffix = identifier && identifier.trim() ? identifier : sanitizedTitle;

  return `product-title${suffix ? '-' + suffix : ''}`;
}

/**
 * Checks if the product has author information to display it on the product card.
 *
 * @param product
 */
export function hasAuthorInfo(product: any): boolean {
  return Array.isArray(product.contributors) && product.contributors.length > 0;
}

/**
 * Verifies if the product is a redeemable ebook to display the redeem button.
 *
 * @param product
 */
export function isRedeemableProduct(product: any): boolean {
  const offerType = (product as ProductCardData).offerType;
  return OfferTypeChecker.isEbook(offerType) && !OfferTypeChecker.isMhpEbook(offerType);
}

/**
 * Populates the local storage object for the PDP.
 * Emits the product view analytics event and navigates to the product page.
 * This is called when clicking on the product card title.
 *
 * @param event
 * @param element
 * @param productIndex
 * @param productInfo
 * @param storeCategory
 * @param selectedCatalog
 * @param orderType
 */
export function populatePdpLocalStorageObject(
  event: MouseEvent,
  element: Element,
  productIndex: number,
  productInfo: ProductCardData,
  storeCategory?: string,
  selectedCatalog?: string,
  orderType?: string
): void {
  const state = store.getState() as StoreState;
  const target = event.target as HTMLAnchorElement;
  const currentPageIndex = selectCurrentPageIndex(state);
  const currentPageLength = selectCurrentPageLength(state);
  const programSourceId = selectProductProgramIdForSearchAnalytics(productInfo);
  // dont capture the pdpLocalStorageObject if the product is clicked from the cart page
  if (!window?.location?.href?.includes('cart')) {
    savePdpLocalStorageObject({
      searchComponentQuery: false,
      searchTerm: '',
      programSourceId,
      pageOffset: currentPageIndex || 1,
      index: productIndex! + 1 || 1,
      currentPageLength,
    });
  }
  emitProductViewAnalyticsEvent(element, productInfo, storeCategory, selectedCatalog, orderType);

  if (target.tagName === 'A' && target.href) {
    if (event.metaKey || event.ctrlKey) {
      // browser handles opening in a new tab
      return;
    }
    event.preventDefault();
    window.location.href = target.href;
  }
}

/**
 * Selects the appropriate program ID for search analytics.
 *
 * @param productInfo
 */
function selectProductProgramIdForSearchAnalytics(productInfo: ProductCardData) {
  return fetchParentProductProgramId(productInfo.id || productInfo?.programSourceId!)
    ? fetchParentProductProgramId(productInfo.id || productInfo?.programSourceId!)
    : productInfo.id || productInfo?.programSourceId!;
}

export function capitalizeFormat(format: string): string {
  return format.toLowerCase().replace(/^\w/, c => c.toUpperCase());
}

export function getSmallTitle(product?: ProductCardData | ProductSmallCardData): string {
  if (product && product.title.length > 50) {
    return product.title.substr(0, 50) + '...';
  }
  return product?.title || '';
}

export function renderPdpSmallCardTemplate({
  product,
  isCreateEbook,
  headingLevel,
  showFullTitle,
  store,
  productIndex,
  onShowTitle,
}: PdpSmallCardTemplateParams): TemplateResult | typeof nothing {
  return product !== undefined
    ? html`
      <div class="d-flex row g-2 mb-3">
        <div class="col-3">
          ${getCreateCustomImage({
            altText: product.title,
            createCoverImages: product.createCoverImages,
            identifier: product.isbn13 || '',
            image: product.image,
            isCreateEbook: isCreateEbook!
          })}
        </div>
        <div class="col-9">
          <a href="${product.url}">
            ${heading(
              headingLevel,
              showFullTitle ? product.title : getSmallTitle(product),
              ['mb-1', 'text-capitalize']
            )}
          </a>
          ${product.title.length > 70 && !showFullTitle
            ? html`
                <button
                  class="btn btn-outline-light btn-sm btn-link px-1 py-0"
                  @click=${onShowTitle}
                >
                  Show Title
                </button>
              `
            : nothing}
          <div class="mt-2 d-flex justify-content-between align-items-center">
            ${product.format
              ? html`<span class="fw-bold text-capitalize small-cart-format-title">${capitalizeFormat(product.format)}</span>`
              : nothing}
            <mhecomm-add-to-cart
              ?showRemoveLink=${true}
              ?showQuantity=${false}
              ?showPrice=${false}
              ?showTotal=${false}
              ?hideQuantityInput=${true}
              businessUnit=${ifDefined(product.businessUnit)}
              store=${ifDefined(store)}
              productTitle=${product.title}
              isbn=${product.isbn || product.mhid!}
              isbn13=${product.isbn13 === 'N/A' ? '' : product.isbn13!}
              format=${ifDefined(product?.format)}
              .programSourceId=${product?.programSourceId}
              offerPricingId=${product?.offerPricingId ?? ""}
              offerType=${product?.offerType ?? ""}
              .productIndex=${productIndex! + 1 || 1}
              .durationType=${product?.durationType}
              .edition=${product?.edition}
              .isEvergreenProduct=${product?.isEvergreen}
              ?isVirtual=${product?.isVirtual}
              .duration=${product?.duration}
              .durationPeriod=${product?.durationPeriod}
              ?removeFromCartPreview=${true}
            ></mhecomm-add-to-cart>
          </div>
        </div>
      </div>
    `
    : html``;
}

export function dataLayerDetail(label: string, productData?: ProductCardData | ProductSmallCardData) {
  const state = store.getState() as StoreState;
  return {
    label: label,
    url: window.location.href,
    user: getUserDataForUserAnalytics(state),
    isbn: productData?.isbn,
    isbn13: productData?.isbn13,
    productTitle: productData?.title,
    ...getCartAnalyticsOverrides(state),
  };
}
